import { useCallback, useMemo } from 'react';
import type { TimelineFormItem } from '../components/TimelineItems';
import type { TimelineConfiguredProps, TimelineTypeConfig } from '../types/configTypes';
import type { TimelineItem, TimelineSourceRecord } from '../types/types';
import { TimelineEditorMode, type TimelineEditor } from './useTimelineEditor';

const isTimelineItem = <Source extends TimelineSourceRecord>(
  record: Source,
): record is Source & TimelineItem<Source['type']> => 'occurredAt' in record && typeof record.occurredAt === 'string';

const canAddRecord = <Source extends TimelineSourceRecord>(typeConfig: TimelineTypeConfig<Source>) => {
  const permission = typeConfig.add?.canAdd;
  return typeof permission === 'function' ? permission() : (permission ?? true);
};

const canEditRecord = <Source extends TimelineSourceRecord>(typeConfig: TimelineTypeConfig<Source>, record: Source) => {
  const permission = typeConfig.edit?.canEdit;
  return typeof permission === 'function' ? permission(record) : (permission ?? true);
};

const useTimelineRecords = <Source extends TimelineSourceRecord>(
  props: TimelineConfiguredProps<Source>,
  editor: TimelineEditor,
) => {
  const { config, items: records, reload } = props;

  const items = useMemo<TimelineItem[]>(() => {
    return records.flatMap(record => {
      const typeConfig = config.types[record.type];

      if (!typeConfig) return [];

      const item = typeConfig.mapItem ? typeConfig.mapItem(record) : isTimelineItem(record) ? record : null;

      if (!item) return [];

      return [
        {
          ...typeConfig.appearance,
          ...item,
        },
      ];
    });
  }, [config, records]);

  const handleFormSuccess = useCallback(() => {
    editor.close();
    void reload?.();
  }, [editor, reload]);

  let addForm: TimelineFormItem | undefined;
  let editForm: TimelineFormItem | undefined;

  if (editor.state.mode === TimelineEditorMode.ADD) {
    const typeConfig = config.types[editor.state.type];
    const add = typeConfig?.add;

    if (add && canAddRecord(typeConfig)) {
      const AddForm = add.Form;
      addForm = {
        item: {
          id: `timeline-add-${editor.state.type}`,
          type: editor.state.type,
          occurredAt: '',
          showAnchor: false,
          ...typeConfig.appearance,
        },
        content: <AddForm onCancel={editor.close} onSuccess={handleFormSuccess} />,
        tag: add.tag,
        tagColor: add.tagColor,
      };
    }
  }

  if (editor.state.mode === TimelineEditorMode.EDIT) {
    const editState = editor.state;
    const record = records.find(item => item.id === editState.id && item.type === editState.type);
    const item = items.find(mappedItem => mappedItem.id === editState.id);
    const typeConfig = record ? config.types[record.type] : undefined;
    const edit = typeConfig?.edit;

    if (record && item && typeConfig && edit && canEditRecord(typeConfig, record)) {
      const EditForm = edit.Form;
      editForm = {
        item,
        content: <EditForm record={record} onCancel={editor.close} onSuccess={handleFormSuccess} />,
        tag: edit.tag,
        tagColor: edit.tagColor,
      };
    }
  }

  return { items, addForm, editForm };
};

export default useTimelineRecords;
